'use client';
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveImage } from '@web/molecules';
import { RichText } from '../../cms/RichText';
import { Modal } from '../Modal';
import { NutritionalInfo } from '../NutritionalInfo';
import {
  Body,
  Container,
  Headline,
  Subheadline,
  TabNav,
  TabButton,
  Content,
  LargeImageContainer,
  MobileImageContainer,
  StyledIngredientModal,
} from './styles';

const tabs = {
  ingredients: 'full ingredients',
  nutrition: 'nutrition',
};

/**
 * CMS-driven recipe modal data (recipeModalsList) with hardcoded fallback
 * modals, then wires up the ingredient modal state. Replaces the resolvedModals
 * useMemo + useIngredientModal pattern used across meal plan form components.
 */
export const useResolvedIngredientModal = (modals, recipeModalsList) => {
  const resolvedModals = useMemo(() => {
    if (!recipeModalsList?.length) return modals;
    const cmsModals = recipeModalsList.map(item => ({
      value: item.slug,
      headline: item.display_name,
      image: {
        src: item.image?.filename,
        alt: item.image?.alt,
        width: 420,
        height: 500,
      },
      fullIngredients: <RichText content={item.full_ingredients?.body} />,
      nutritionalInfo: (
        item.nutritional_information?.body?.nutrients?.nutrients ?? []
      ).map(n => ({ label: n.label, value: n.value })),
      body: (
        <RichText content={item.nutritional_information?.body?.description} />
      ),
    }));
    return modals.map(
      fallback =>
        cmsModals.find(cms => cms.value === fallback.value) ?? fallback,
    );
  }, [recipeModalsList, modals]);

  return useIngredientModal(resolvedModals);
};

export const useIngredientModal = modals => {
  const [modal, setModal] = useState({ shown: false, value: 'beef' });
  const currentModal = modals.find(cont => cont.value === modal.value);

  const onRecipeDetailsClick = recipe => {
    setModal({ shown: true, value: recipe });
  };

  const onClickClose = () => {
    setModal(prev => ({ ...prev, shown: false }));
  };

  return {
    // The CMS schema adds `shown: true` for preview mode,
    // so we need to overwrite with local value.
    // TODO(James) maybe a better way of setting preview-specific props?
    modal: { ...currentModal, ...modal, onClickClose },
    onRecipeDetailsClick,
  };
};

export const IngredientModal = ({
  headline,
  fullIngredients,
  nutritionalInfo,
  body,
  onClickClose,
  shown,
  image,
}) => {
  const [activeTab, setActiveTab] = useState(tabs.ingredients);

  return (
    <Modal
      shown={shown}
      onCloseModal={onClickClose}
      ariaLabel="Full ingredients modal"
      usePlumXCloseButton
    >
      <StyledIngredientModal>
        <Container>
          <LargeImageContainer>
            <ResponsiveImage {...image} />
          </LargeImageContainer>
          <Content>
            <Headline>{headline}</Headline>
            <MobileImageContainer>
              <ResponsiveImage {...image} />
            </MobileImageContainer>
            <TabNav role="tablist">
              <TabButton
                onClick={() => setActiveTab(tabs.ingredients)}
                active={activeTab === tabs.ingredients}
                role="tab"
                aria-expanded={activeTab === tabs.ingredients}
              >
                {tabs.ingredients}
              </TabButton>
              <TabButton
                onClick={() => setActiveTab(tabs.nutrition)}
                active={activeTab === tabs.nutrition}
                role="tab"
                aria-expanded={activeTab === tabs.nutrition}
              >
                {tabs.nutrition}
              </TabButton>
            </TabNav>
            {activeTab === tabs.nutrition ? (
              <>
                <Subheadline>Guaranteed Analysis</Subheadline>
                <NutritionalInfo
                  nutritionalInfo={nutritionalInfo}
                  role="tabpanel"
                  id="tabpanel-nutrition"
                  ariaLabelledby="tab-nutrition"
                  hidden={activeTab !== tabs.nutrition}
                />
                <Body>{body}</Body>
              </>
            ) : (
              <Body>{fullIngredients}</Body>
            )}
          </Content>
        </Container>
      </StyledIngredientModal>
    </Modal>
  );
};

IngredientModal.displayName = 'IngredientModal';

IngredientModal.propTypes = {
  headline: PropTypes.string.isRequired,
  fullIngredients: PropTypes.node.isRequired,
  nutritionalInfo: PropTypes.array.isRequired,
  body: PropTypes.node.isRequired,
  onClickClose: PropTypes.func.isRequired,
  shown: PropTypes.bool.isRequired,
  image: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
};
